<template>
    <div
        class="mp-dock-tabs"
        :class="tabsClass"
        :data-node-id="node.id"
    >
        <DockPanelChrome
            mode="group"
            :group-node-id="node.id"
            :group-panel-ids="node.panelIds"
            :panel-id="activePanelId"
            :title="groupTitle"
            :icon="groupIcon"
            :pinned="groupPinned"
            :closable="false"
            :show-pin="false"
            @drag-start="(p) => $emit('drag-start', p)"
        />
        <div
            v-if="showTabStrip"
            class="mp-dock-tab-strip"
            :class="stripClass"
        >
            <Container
                class="mp-dock-tab-dnd-container"
                orientation="horizontal"
                lock-axis="x"
                :group-name="dndGroup"
                :get-child-payload="getTabPayload"
                drag-handle-selector=".mp-dock-tab-reorder-handle"
                @drop="onTabDrop"
            >
                <Draggable
                    v-for="pid in node.panelIds"
                    :key="pid"
                    class="mp-dock-tab-draggable"
                >
                    <div
                        class="mp-dock-tab-item"
                        :class="{ 'mp-dock-tab-item--active': pid === activePanelId }"
                        @click="selectTab(pid)"
                    >
                        <span
                            class="mp-dock-tab-reorder-handle"
                            aria-label="Drag to reorder tabs"
                            @click.stop
                        >
                            <QIcon name="mdi-drag" size="xs" />
                        </span>
                        <span
                            class="mp-dock-tab-title"
                            @pointerdown="(e) => onTabDockDragStart(e, pid)"
                        >
                            <QIcon
                                v-if="panelMeta(pid)?.icon"
                                :name="panelMeta(pid).icon"
                                size="xs"
                                class="q-mr-xs"
                            />
                            {{ panelMeta(pid)?.title || pid }}
                        </span>
                        <QBtn
                            v-if="panelMeta(pid)?.closable !== false"
                            flat
                            dense
                            round
                            size="xs"
                            icon="mdi-close"
                            class="q-ml-xs"
                            @click.stop="hideTab(pid)"
                        />
                    </div>
                </Draggable>
            </Container>
        </div>
        <div
            class="mp-dock-tab-panels"
            :data-dock-host-id="node.id"
            data-dock-host-type="tabs"
            :data-dock-host-label="groupTitle"
        >
            <div
                v-if="isDynamicContent"
                ref="dynamicContentHost"
                class="mp-dock-panel-host mp-dock-dynamic-content"
            />
            <DockPanelLeaf
                v-else-if="activePanelId"
                :key="activePanelId"
                :panel-id="activePanelId"
                :node-id="`${node.id}-${activePanelId}`"
                exclude-dock-host
                exclude-chrome
                :meta="panelMeta(activePanelId)"
                :collapsed="isPanelCollapsed(activePanelId)"
                :is-pinned="isPanelPinned(activePanelId)"
                @hide="hideTab"
                @toggle-pin="(id) => $emit('toggle-pin', id)"
                @drag-start="(p) => $emit('drag-start', p)"
                @expand="expandPanel"
                @focus-change="(p) => $emit('focus-change', p)"
            />
        </div>
    </div>
</template>

<script>
import { QBtn, QIcon } from 'quasar';
import { Container, Draggable } from 'vue3-smooth-dnd';
import {
    MULTIPANEL_DOCK_DND_GROUP,
    TAB_PLACEMENT_BOTTOM,
    TAB_PLACEMENT_TOP,
} from './constants.js';
import DockPanelChrome from './DockPanelChrome.vue';
import DockPanelLeaf from './DockPanelLeaf.vue';

export default {
    name: 'DockTabGroup',
    components: { Container, Draggable, DockPanelLeaf, DockPanelChrome, QBtn, QIcon },

    props: {
        node: { type: Object, required: true },
    },

    emits: ['drag-start', 'toggle-pin', 'focus-change'],

    inject: {
        multipanelDockLayout: { default: null },
    },

    data() {
        return {
            dndGroup: MULTIPANEL_DOCK_DND_GROUP,
        };
    },

    computed: {
        tabPlacement() {
            return this.node.tabPlacement || TAB_PLACEMENT_BOTTOM;
        },
        tabsClass() {
            return {
                'mp-dock-tabs--tabsTop': this.tabPlacement === TAB_PLACEMENT_TOP,
                'mp-dock-tabs--tabsBottom': this.tabPlacement === TAB_PLACEMENT_BOTTOM,
            };
        },
        stripClass() {
            return {
                'mp-dock-tab-strip--top': this.tabPlacement === TAB_PLACEMENT_TOP,
                'mp-dock-tab-strip--bottom': this.tabPlacement === TAB_PLACEMENT_BOTTOM,
            };
        },
        showTabStrip() {
            return (this.node.panelIds || []).length > 1;
        },
        activePanelId() {
            const ids = this.node.panelIds || [];
            if (ids.includes(this.node.activePanelId)) {
                return this.node.activePanelId;
            }
            return ids[0] || null;
        },
        groupTitle() {
            if (this.node.title) return this.node.title;
            const active = this.activePanelId;
            if (active) {
                return this.panelMeta(active)?.title || active;
            }
            return 'Tab group';
        },
        groupIcon() {
            const active = this.activePanelId;
            return active ? this.panelMeta(active)?.icon : null;
        },
        groupPinned() {
            const active = this.activePanelId;
            return active ? this.isPanelPinned(active) : true;
        },
        isDynamicContent() {
            return this.node.contentMode === 'dynamic';
        },
        contentPanelId() {
            return this.node.contentPanelId || `${this.node.id}__content`;
        },
    },

    watch: {
        activePanelId() {
            if (this.isDynamicContent) {
                this.$nextTick(() => this.mountDynamicContent());
            }
        },
    },

    mounted() {
        if (this.isDynamicContent) {
            this.$nextTick(() => this.mountDynamicContent());
        }
    },

    updated() {
        if (this.isDynamicContent) {
            this.$nextTick(() => this.mountDynamicContent());
        }
    },

    beforeUnmount() {
        if (this.isDynamicContent) {
            this.multipanelDockLayout?.unmountPanelFromTarget?.(
                this.contentPanelId,
            );
        }
    },

    methods: {
        mountDynamicContent() {
            const el = this.$refs.dynamicContentHost;
            if (!el || !this.multipanelDockLayout) return;
            this.multipanelDockLayout.mountPanelToTarget(
                this.contentPanelId,
                el,
            );
        },
        panelMeta(panelId) {
            return this.multipanelDockLayout?.getPanelMeta?.(panelId) || {};
        },

        isPanelPinned(panelId) {
            return this.multipanelDockLayout?.isPanelPinned?.(panelId) ?? true;
        },

        isPanelCollapsed(panelId) {
            return this.multipanelDockLayout?.isPanelCollapsed?.(panelId) ?? false;
        },

        getTabPayload(index) {
            const panelId = this.node.panelIds[index];
            return { panelId, tabsId: this.node.id, index };
        },

        selectTab(panelId) {
            this.multipanelDockLayout?.setActiveTab?.(this.node.id, panelId);
        },

        hideTab(panelId) {
            const close = this.multipanelDockLayout?.closePanel;
            if (close) close(panelId);
            else this.multipanelDockLayout?.hidePanel?.(panelId);
        },

        expandPanel(panelId) {
            this.multipanelDockLayout?.expandPanel?.(panelId);
        },

        onTabDrop(dropResult) {
            if (!dropResult) return;
            this.multipanelDockLayout?.onTabDrop?.({
                ...dropResult,
                targetTabsId: this.node.id,
            });
        },

        onTabDockDragStart(e, panelId) {
            if (e.pointerType === 'mouse' && e.button !== 0) return;
            if (e.target.closest('.q-btn, .mp-dock-tab-reorder-handle')) return;
            e.preventDefault();
            const meta = this.panelMeta(panelId);
            this.multipanelDockLayout?.startDockDrag?.({
                dragMode: 'panel',
                panelId,
                title: meta?.title || panelId,
                clientX: e.clientX,
                clientY: e.clientY,
                pointerId: e.pointerId,
            });
        },
    },
};
</script>
